/**
 * FeeCollectionDialog — shared modal showing student fee summary +
 * collect form. Used by school_admin (Students page, Payments page) and
 * teacher (Collect page).
 *
 * Phases: opens with `student_id`. Loads /school/students/{id}/fee-summary,
 * lets user pick a head, enter amount + payment mode, posts to
 * /school/fee-payments with an auto-generated idempotency_key.
 * On success shows receipt preview with Print + WhatsApp share button.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Receipt,
  CurrencyInr,
  WhatsappLogo,
  Printer,
  CheckCircle,
  ArrowLeft,
  FilePdf,
} from "@phosphor-icons/react";
import api, { API_BASE, cleanPayload, formatApiError } from "@/lib/api";
import { printPdf } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import ReceiptCard from "@/components/ReceiptCard";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function publicPdfUrl(payment) {
  if (!payment) return null;
  // Phase 18.4 — prefer the backend-rendered absolute PDF URL. It is always
  // routed through `/api/public/...` so it reaches the FastAPI backend
  // regardless of which custom domain the user visits.
  if (payment.share_pdf_url) return payment.share_pdf_url;
  // Defensive: build the backend route from the slug if for any reason the
  // backend didn't include `share_pdf_url` (older deploys).
  if (payment.public_slug) {
    return `${API_BASE}/public/r/${payment.public_slug}/pdf`;
  }
  // Final fallback: legacy stateless-token URL (still supported).
  if (payment.id && payment.share_token) {
    return `${API_BASE}/public/receipts/${payment.id}.pdf?t=${payment.share_token}`;
  }
  return null;
}

function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function FeeCollectionDialog({ open, onOpenChange, studentId, onCollected }) {
  const [summary, setSummary] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [selectedHeadId, setSelectedHeadId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(""); // YYYY-MM (only for monthly heads)
  const [monthly, setMonthly] = useState(null); // monthly-summary response
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [txnRef, setTxnRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [receipt, setReceipt] = useState(null);

  const reset = useCallback(() => {
    setSelectedHeadId("");
    setSelectedMonth("");
    setAmount("");
    setMode("cash");
    setTxnRef("");
    setNotes("");
    setError("");
    setReceipt(null);
    setIdempotencyKey(generateUUID());
  }, []);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError("");
    try {
      const [s, sc, ms] = await Promise.all([
        api.get(`/school/students/${studentId}/fee-summary`),
        api.get("/school/me"),
        api.get(`/school/students/${studentId}/monthly-summary`),
      ]);
      setSummary(s.data);
      setSchool(sc.data);
      setMonthly(ms.data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [studentId]);

  useEffect(() => {
    if (open) {
      reset();
      load();
    }
  }, [open, load, reset]);

  const selectedHead = useMemo(
    () => summary?.heads?.find((h) => h.fee_structure_id === selectedHeadId),
    [summary, selectedHeadId],
  );

  const isMonthlyHead = selectedHead?.frequency === "monthly";

  const selectedHeadMonthly = useMemo(
    () => monthly?.heads?.find((h) => h.fee_structure_id === selectedHeadId),
    [monthly, selectedHeadId],
  );

  const selectedMonthBlock = useMemo(
    () => selectedHeadMonthly?.months?.find((m) => m.month === selectedMonth),
    [selectedHeadMonthly, selectedMonth],
  );

  const effectiveBalance = isMonthlyHead
    ? (selectedMonthBlock?.balance ?? 0)
    : (selectedHead?.balance ?? 0);

  // Reset month when switching heads
  useEffect(() => { setSelectedMonth(""); }, [selectedHeadId]);

  // Auto-pick first head with balance when summary loads
  useEffect(() => {
    if (summary && !selectedHeadId) {
      const firstWithBalance = summary.heads.find((h) => h.balance > 0);
      if (firstWithBalance) {
        setSelectedHeadId(firstWithBalance.fee_structure_id);
        // For non-monthly heads we can prefill the balance amount; for monthly,
        // wait until the user picks a month.
        if (firstWithBalance.frequency !== "monthly") {
          setAmount(String(firstWithBalance.balance));
        }
      }
    }
  }, [summary, selectedHeadId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedHead) {
      setError("Select a fee head");
      return;
    }
    if (isMonthlyHead && !selectedMonth) {
      setError("Pick a month to pay for");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Enter a valid amount");
      return;
    }
    if (amt > effectiveBalance + 0.001) {
      setError(`Amount exceeds balance (${formatINR(effectiveBalance)})`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(
        "/school/fee-payments",
        cleanPayload({
          student_id: studentId,
          fee_structure_id: selectedHeadId,
          amount_paid: amt,
          payment_mode: mode,
          txn_ref: txnRef,
          notes,
          idempotency_key: idempotencyKey,
          period_month: isMonthlyHead ? selectedMonth : undefined,
        }),
      );
      setReceipt(data);
      toast.success(`Receipt ${data.receipt_no} created`);
      onCollected?.(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  const collectAnother = () => {
    // Refresh summary to get new balances; reset form
    reset();
    load();
  };

  const print = () => {
    // Phase P1 — Print the backend-generated 1-page PDF via hidden iframe.
    // Previously `window.print()` printed the entire ERP DOM (sidebar +
    // dialog chrome + tables) → 6 A4 pages for a single receipt. Now uses
    // the same PDF that Download & WhatsApp share deliver → exactly 1 page.
    const url = receipt && publicPdfUrl(receipt);
    printPdf(url);
  };

  const downloadPdf = () => {
    const url = receipt && publicPdfUrl(receipt);
    if (url) window.open(url, "_blank", "noopener");
  };

  const whatsappShare = async (lang = "en") => {
    if (!receipt) return;
    let phone = (summary?.student?.parent_phone || "").replace(/[^\d+]/g, "");
    let message = "";
    try {
      const r = await api.get(
        `/school/fee-payments/${receipt.id}/share-message?lang=${lang}`,
      );
      message = r.data?.message || "";
      phone = (r.data?.parent_phone || phone).replace(/[^\d+]/g, "");
    } catch (e) {
      // Fallback to a minimal message if the endpoint hiccups
      const pdfUrl = publicPdfUrl(receipt);
      message =
        `Receipt ${receipt.receipt_no} from ${school?.school_name || "School"}\n` +
        `${receipt.head_snapshot}: ${formatINR(receipt.amount_paid)}\n` +
        `Mode: ${receipt.payment_mode.toUpperCase()}` +
        (receipt.txn_ref ? `\nTxn Ref: ${receipt.txn_ref}` : "") +
        (pdfUrl ? `\n\nDownload PDF: ${pdfUrl}` : "");
    }
    const text = encodeURIComponent(message);
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl print:max-w-none print:shadow-none" data-testid="fee-collection-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt size={20} weight="duotone" />
            {receipt ? "Payment received" : "Collect fees"}
          </DialogTitle>
          <DialogDescription>
            {receipt
              ? "Print or share this receipt with the parent."
              : "Select a fee head and enter the amount being collected."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : error && !summary ? (
          <div className="py-8 text-center text-sm text-red-600">{error}</div>
        ) : receipt ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle size={16} weight="fill" /> Payment recorded successfully
            </div>
            <ReceiptCard school={school} payment={receipt} />
            <div className="flex flex-wrap gap-2 print:hidden">
              <Button variant="outline" onClick={collectAnother} className="gap-1.5 h-11 md:h-9" data-testid="collect-another-btn">
                <ArrowLeft size={14} /> Collect another
              </Button>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={downloadPdf}
                className="gap-1.5 h-11 md:h-9"
                data-testid="download-pdf-btn"
              >
                <FilePdf size={14} weight="fill" /> Download PDF
              </Button>
              <Button variant="outline" onClick={print} className="gap-1.5 h-11 md:h-9" data-testid="print-receipt-btn">
                <Printer size={14} /> Print
              </Button>
              <Button
                onClick={() => whatsappShare("en")}
                className="bg-green-600 hover:bg-green-700 gap-1.5 h-11 md:h-9"
                data-testid="whatsapp-share-btn"
              >
                <WhatsappLogo size={14} weight="fill" /> Share on WhatsApp
              </Button>
              <Button
                onClick={() => whatsappShare("hi")}
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-50 hover:text-green-700 gap-1.5 px-2 h-11 md:h-9"
                title="Share message in Hindi"
                data-testid="whatsapp-share-hi-btn"
              >
                <WhatsappLogo size={14} weight="fill" /> हिंदी
              </Button>
            </div>
          </div>
        ) : summary ? (
          <form onSubmit={submit} className="space-y-4">
            {/* Student summary */}
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-700">
                  {(summary.student.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{summary.student.name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {summary.student.admission_no} ·{" "}
                    {summary.student.class_name}{summary.student.class_section ? ` - ${summary.student.class_section}` : ""}
                    {" "}· {summary.student.session_name}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-200">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total fee</div>
                  <div className="font-mono text-xs sm:text-sm text-slate-900">{formatINR(summary.totals.amount)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Paid</div>
                  <div className="font-mono text-xs sm:text-sm text-emerald-700">{formatINR(summary.totals.paid)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Balance</div>
                  <div className="font-mono text-xs sm:text-sm text-amber-700" data-testid="student-balance">
                    {formatINR(summary.totals.balance)}
                  </div>
                </div>
              </div>
            </div>

            {/* Fee head selector */}
            <div>
              <Label>Fee head *</Label>
              {summary.heads.length === 0 ? (
                <div className="mt-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  No fee structure defined for this class. Add one from <strong>Fee Structure</strong> page first.
                </div>
              ) : (
                <div className="mt-1.5 space-y-2" data-testid="fee-heads-list">
                  {summary.heads.map((h) => {
                    const isSelected = selectedHeadId === h.fee_structure_id;
                    // For monthly heads, use the year-wide totals from monthly_summary
                    const monthlyHead = monthly?.heads?.find(
                      (m) => m.fee_structure_id === h.fee_structure_id,
                    );
                    const yearAmount = monthlyHead?.expected_total ?? h.amount;
                    const yearPaid = monthlyHead?.paid_total ?? h.paid;
                    const yearBalance = monthlyHead?.pending_total ?? h.balance;
                    const isFullPaid =
                      h.frequency === "monthly"
                        ? yearBalance <= 0.001
                        : h.status === "paid";
                    return (
                      <button
                        type="button"
                        key={h.fee_structure_id}
                        disabled={isFullPaid}
                        onClick={() => {
                          setSelectedHeadId(h.fee_structure_id);
                          if (h.frequency !== "monthly") {
                            setAmount(String(h.balance));
                          } else {
                            setAmount(""); // wait for month selection
                          }
                        }}
                        data-testid={`fee-head-option-${h.fee_structure_id}`}
                        className={`w-full text-left px-3 py-3 rounded-md border transition-colors ${
                          isFullPaid
                            ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
                            : isSelected
                            ? "border-blue-900 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 text-sm">
                              {h.head}
                              {h.frequency === "monthly" && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-semibold uppercase tracking-wider">
                                  Monthly
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">
                              {h.frequency === "monthly"
                                ? `${formatINR(h.amount)} / month · year total ${formatINR(yearAmount)} · paid ${formatINR(yearPaid)}`
                                : `${formatINR(h.amount)} total · ${formatINR(yearPaid)} paid`}
                            </div>
                            {h.concession > 0 && (
                              <div
                                className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[10px] font-medium"
                                data-testid={`head-effective-${h.fee_structure_id}`}
                                title={`Concession applied — net payable ${formatINR(h.effective_amount)}`}
                              >
                                Net payable {formatINR(h.effective_amount)}
                                {h.frequency_normalized === "monthly" ? " / month" : ""}
                                {h.concession_reason && (
                                  <span className="opacity-70 uppercase tracking-wider">
                                    · {h.concession_reason}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {isFullPaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700">
                                <CheckCircle size={12} weight="fill" /> Paid
                              </span>
                            ) : (
                              <>
                                <div className="font-mono text-sm font-semibold text-amber-700">
                                  {formatINR(yearBalance)}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                  {h.frequency === "monthly" ? "Year balance" : "Balance"}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedHead && isMonthlyHead && selectedHeadMonthly && (
              <div data-testid="month-strip-section">
                <Label>Pay for which month? *</Label>
                <div className="text-[11px] text-slate-500 mt-1 mb-2">
                  Tap a month to pre-fill the balance. Green = paid · Yellow = partial · Red = pending.
                  {selectedHeadMonthly.legacy?.paid > 0 && (
                    <span className="block mt-1 text-slate-600">
                      <strong>Legacy:</strong> {formatINR(selectedHeadMonthly.legacy.paid)} collected before month-tracking ({selectedHeadMonthly.legacy.count} receipt{selectedHeadMonthly.legacy.count === 1 ? "" : "s"}). Not assigned to a specific month.
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid="month-strip">
                  {(() => {
                    // Phase 27E — months with status="na" are pre-join months
                    // (student's fee_start_month is later than this month).
                    // They render greyed-out, are non-clickable, and are EXCLUDED
                    // from the sequential-month "earlier unpaid" calculation.
                    //
                    // Sequential-month rule (matches backend): a month is locked
                    // if any EARLIER LIABLE month in the strip is still due/partial.
                    const months = selectedHeadMonthly.months;
                    const blockedFromIdx = months.findIndex(
                      (m) => m.status !== "paid" && m.status !== "na" && m.balance > 0
                    );
                    return months.map((m, idx) => {
                      const isNa = m.status === "na";
                      const isSelected = selectedMonth === m.month;
                      const isPaid = m.status === "paid";
                      const isLockedByEarlierUnpaid =
                        !isPaid && !isNa && blockedFromIdx >= 0 && idx > blockedFromIdx;
                      const cls = isNa
                        ? "bg-slate-50 text-slate-400 border-slate-200 border-dashed"
                        : isPaid
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : m.status === "partial"
                        ? "bg-amber-100 text-amber-800 border-amber-300"
                        : isLockedByEarlierUnpaid
                        ? "bg-slate-100 text-slate-400 border-slate-200"
                        : "bg-red-50 text-red-800 border-red-200";
                      const ring = isSelected ? "ring-2 ring-blue-900 ring-offset-1" : "";
                      const disabled = isPaid || isLockedByEarlierUnpaid || isNa;
                      const title = isNa
                        ? `${m.label}: Not applicable — student joined later`
                        : isLockedByEarlierUnpaid
                        ? `Locked — clear earlier unpaid months first`
                        : `${m.label}: paid ${formatINR(m.paid)}, balance ${formatINR(m.balance)}`;
                      return (
                        <button
                          type="button"
                          key={m.month}
                          disabled={disabled}
                          onClick={() => {
                            if (isNa) return;
                            setSelectedMonth(m.month);
                            setAmount(String(m.balance));
                          }}
                          title={title}
                          data-testid={`month-cell-${m.month}`}
                          data-status={m.status}
                          data-locked={isLockedByEarlierUnpaid ? "1" : "0"}
                          className={`px-2 py-2 rounded border text-[11px] font-semibold transition-all min-h-[44px] md:min-h-0 ${cls} ${ring} ${
                            disabled ? "cursor-not-allowed opacity-70" : "hover:opacity-90"
                          }`}
                        >
                          <div className="leading-tight">{m.label.split(" ")[0]}</div>
                          <div className="text-[9px] font-mono opacity-75">
                            {isNa ? "N/A" : m.label.split(" ")[1]}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
                {(() => {
                  const months = selectedHeadMonthly.months;
                  const blockedFromIdx = months.findIndex(
                    (m) => m.status !== "paid" && m.status !== "na" && m.balance > 0
                  );
                  if (blockedFromIdx < 0 || blockedFromIdx >= months.length - 1) return null;
                  const firstPending = months[blockedFromIdx].label;
                  return (
                    <div
                      className="mt-2 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
                      data-testid="sequential-month-warning"
                    >
                      <span className="font-semibold shrink-0">Sequential rule:</span>
                      <span>
                        Pay <strong>{firstPending}</strong> first. Future months unlock once earlier ones are cleared.
                      </span>
                    </div>
                  );
                })()}
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <div>
                    Year total: <span className="font-mono">{formatINR(selectedHeadMonthly.expected_total)}</span>
                    {" · "}Paid: <span className="font-mono text-emerald-700">{formatINR(selectedHeadMonthly.paid_total)}</span>
                    {" · "}Pending: <span className="font-mono text-amber-700">{formatINR(selectedHeadMonthly.pending_total)}</span>
                  </div>
                  {selectedMonth && (
                    <div data-testid="selected-month-info">
                      Selected: <strong>{selectedMonthBlock?.label}</strong> · balance{" "}
                      <span className="font-mono">{formatINR(selectedMonthBlock?.balance || 0)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedHead && (effectiveBalance > 0 || (isMonthlyHead && selectedMonth)) && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Amount paid *</Label>
                    <div className="relative mt-1.5">
                      <CurrencyInr
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={effectiveBalance}
                        required
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-9 font-mono"
                        data-testid="collect-amount-input"
                      />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Max {formatINR(effectiveBalance)}
                      {isMonthlyHead && selectedMonth && ` · for ${selectedMonthBlock?.label}`}
                    </div>
                  </div>
                  <div>
                    <Label>Payment mode *</Label>
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger className="mt-1.5" data-testid="collect-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash" data-testid="mode-cash">Cash</SelectItem>
                        <SelectItem value="upi" data-testid="mode-upi">UPI</SelectItem>
                        <SelectItem value="bank" data-testid="mode-bank">Bank transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(mode === "upi" || mode === "bank") && (
                  <div>
                    <Label>Transaction ref / UTR</Label>
                    <Input
                      value={txnRef}
                      onChange={(e) => setTxnRef(e.target.value)}
                      placeholder="e.g. UPI ref or bank UTR"
                      className="mt-1.5 font-mono"
                      data-testid="collect-txn-ref-input"
                    />
                  </div>
                )}

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional"
                    className="mt-1.5"
                  />
                </div>
              </>
            )}

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="h-11 md:h-9">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting
                  || !selectedHead
                  || (selectedHead?.frequency === "monthly"
                      ? (selectedHeadMonthly?.pending_total ?? 0) <= 0.001
                      : selectedHead?.balance <= 0)
                }
                className="bg-blue-900 hover:bg-blue-800 h-11 md:h-9"
                data-testid="collect-submit-btn"
              >
                {submitting ? "Recording…" : `Collect ${amount ? formatINR(parseFloat(amount) || 0) : ""}`}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
